import { useOptimisticMutation } from '@noor/lib';
import {
  venueApi,
  venueKeys,
  useVenueScope,
  type Venue,
  type VenueUpdateInput,
  type VenueUpdateResult,
} from '@/entities/venue';

/**
 * Optimistically save the venue's weekly working hours; the settle-time
 * invalidation reconciles the cache, and the helper rolls back on failure.
 *
 * `branchId` must be the branch the screen is SHOWING, not the session's active
 * one: the branches list opens any branch's venue screen by id, and defaulting
 * to the active branch here would write one branch's hours onto another.
 *
 * The result carries `pending: true` when the write was queued behind
 * moderation — the stored schedule then stays unchanged until a moderator
 * approves it (and the reconciling refetch reverts the optimistic patch), so
 * the modal surfaces the queued case as its own toast.
 */
export function useUpdateVenueHours(branchId?: string) {
  const scope = useVenueScope(branchId);
  return useOptimisticMutation<VenueUpdateResult, VenueUpdateInput, Venue>({
    meta: { suppressErrorToast: true },
    queryKey: venueKeys.profile(scope.restaurantId),
    mutationFn: (input) => venueApi.update(scope, input),
    update: (venue, input) => ({ ...venue, workingHours: input.workingHours }),
  });
}
